import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Clock, Tag, Share2 } from 'lucide-react';
import SEO from '../components/SEO';
import blogArticles from '../data/blogArticles';

const BlogArticlePage = () => {
  const { slug } = useParams();
  const article = blogArticles.find((a) => a.slug === slug);

  if (!article) return <Navigate to="/blog" replace />;

  const currentIndex = blogArticles.indexOf(article);
  const prevArticle = currentIndex > 0 ? blogArticles[currentIndex - 1] : null;
  const nextArticle =
    currentIndex < blogArticles.length - 1 ? blogArticles[currentIndex + 1] : null;

  return (
    <div className="pt-20">
      <SEO
        title={article.metaTitle}
        description={article.description}
        path={`/blog/${article.slug}`}
        keywords={article.keywords}
        type="article"
        breadcrumbs={[
          { name: 'Startseite', url: '/' },
          { name: 'Blog', url: '/blog' },
          { name: article.title },
        ]}
      />

      <article>
        <HeaderSection article={article} />
        <ContentSection article={article} />
        <NavigationSection prev={prevArticle} next={nextArticle} />
      </article>

      <CTASection />
    </div>
  );
};

const HeaderSection = ({ article }) => (
  <section className="section bg-gray-50 relative overflow-hidden">
    <div className="absolute inset-0 grid-pattern" />
    <div className="container-custom relative z-10">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/blog"
          data-testid="back-to-blog"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-emerald-600 transition-colors mb-8 fade-up"
        >
          <ArrowLeft className="w-4 h-4" /> Alle Beiträge
        </Link>

        <div className="flex items-center gap-3 mb-6 fade-up">
          <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
            <Tag className="w-3 h-3" />
            {article.category}
          </span>
          <span className="flex items-center gap-1 text-xs text-gray-400">
            <Clock className="w-3 h-3" />
            {article.readTime}
          </span>
          <time className="text-xs text-gray-400" dateTime={article.date}>
            {new Date(article.date).toLocaleDateString('de-DE', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </time>
        </div>

        <div className="reveal-container">
          <h1
            data-testid="article-title"
            className="heading-xl text-3xl md:text-4xl lg:text-5xl text-gray-900 reveal-text mb-6 leading-tight"
          >
            {article.title}
          </h1>
        </div>

        <p className="body-text text-base md:text-lg text-gray-500 fade-up">
          {article.description}
        </p>
      </div>
    </div>
  </section>
);

const ContentSection = ({ article }) => (
  <section className="py-16 md:py-20 bg-white">
    <div className="container-custom">
      <div className="max-w-3xl mx-auto">
        {article.content.map((block, i) => (
          <ContentBlock key={i} block={block} index={i} />
        ))}

        <div className="mt-12 pt-8 border-t border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
              <span className="text-white font-bold text-sm">L</span>
            </div>
            <div>
              <span className="block text-sm font-semibold text-gray-900">
                LAIzyones Redaktion
              </span>
              <span className="block text-xs text-gray-400">
                KI-Agentur Magdeburg & Braunschweig
              </span>
            </div>
          </div>
          <button
            data-testid="share-btn"
            onClick={() => {
              if (navigator.share) {
                navigator.share({
                  title: article.title,
                  url: window.location.href,
                });
              } else {
                navigator.clipboard.writeText(window.location.href);
              }
            }}
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-emerald-600 transition-colors"
          >
            <Share2 className="w-4 h-4" />
            Teilen
          </button>
        </div>
      </div>
    </div>
  </section>
);

const ContentBlock = ({ block }) => {
  switch (block.type) {
    case 'intro':
      return (
        <p className="text-gray-700 text-lg leading-relaxed mb-8 font-medium">
          {block.text}
        </p>
      );
    case 'heading':
      return (
        <h2 className="font-['Space_Grotesk'] font-bold text-2xl md:text-3xl text-gray-900 mt-12 mb-5">
          {block.text}
        </h2>
      );
    case 'paragraph':
      return (
        <p className="text-gray-600 text-base leading-relaxed mb-5">
          {block.text}
        </p>
      );
    case 'highlight':
      return (
        <div className="my-6 p-5 rounded-xl bg-emerald-50 border-l-4 border-emerald-500">
          <p className="text-gray-700 text-sm leading-relaxed font-medium">
            {block.text}
          </p>
        </div>
      );
    case 'cta':
      return (
        <div className="my-8 p-6 rounded-2xl bg-gradient-to-br from-[#0A0F14] to-[#111820] text-center">
          <p className="text-gray-300 text-base leading-relaxed mb-5">
            {block.text}
          </p>
          <Link
            to="/#kontakt"
            data-testid="article-cta"
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold px-6 py-3 rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all text-sm"
          >
            Kostenlose Beratung <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      );
    default:
      return null;
  }
};

const NavigationSection = ({ prev, next }) => (
  <section className="bg-gray-50 py-12">
    <div className="container-custom">
      <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4">
        {prev ? (
          <Link
            to={`/blog/${prev.slug}`}
            data-testid="prev-article"
            className="card group hover:border-emerald-200 transition-all p-5"
          >
            <span className="text-xs text-gray-400 uppercase tracking-wider mb-2 block">
              Vorheriger Beitrag
            </span>
            <span className="font-['Space_Grotesk'] font-semibold text-sm text-gray-900 group-hover:text-emerald-600 transition-colors leading-tight block">
              {prev.title}
            </span>
          </Link>
        ) : (
          <div />
        )}
        {next ? (
          <Link
            to={`/blog/${next.slug}`}
            data-testid="next-article"
            className="card group hover:border-emerald-200 transition-all p-5 text-right"
          >
            <span className="text-xs text-gray-400 uppercase tracking-wider mb-2 block">
              Nächster Beitrag
            </span>
            <span className="font-['Space_Grotesk'] font-semibold text-sm text-gray-900 group-hover:text-emerald-600 transition-colors leading-tight block">
              {next.title}
            </span>
          </Link>
        ) : (
          <div />
        )}
      </div>
    </div>
  </section>
);

const CTASection = () => (
  <section className="section bg-white pb-24 md:pb-32">
    <div className="container-custom">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-600 to-cyan-600 p-8 md:p-12 lg:p-16">
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <div className="relative z-10 text-center max-w-3xl mx-auto">
          <h2 className="font-['Space_Grotesk'] font-bold text-3xl md:text-4xl text-white mb-6">
            Mehr als nur Theorie?
          </h2>
          <p className="text-emerald-100 text-base md:text-lg mb-8">
            Erfahren Sie in einem kostenlosen Erstgespräch, wie diese Konzepte
            konkret in Ihrem Unternehmen umgesetzt werden können.
          </p>
          <Link
            to="/#kontakt"
            className="inline-flex items-center justify-center gap-2 bg-white text-emerald-600 font-semibold px-8 py-4 rounded-xl hover:bg-emerald-50 transition-colors"
          >
            Kostenloses Gespräch vereinbaren <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

export default BlogArticlePage;
